/**
 * Drei Speicherplaetze fuer Wellen (VISUAL_REFERENCE Abschnitt 44).
 *
 * Produktentscheidung vom 27.09.2026: Die Plaetze liegen im Browser des
 * Spielers und ueberleben damit das Match und einen Neuladen der Seite. Eine
 * Welle, die man sich zurechtgelegt hat, will man vor allem im NAECHSTEN Match
 * wieder haben; innerhalb eines Matches gibt es dafuer schon LAST WAVE.
 *
 * Ausdruecklich mit Blick auf spaeter gebaut: Sobald es Konten gibt, sollen
 * die Plaetze am Spieler haengen statt am Geraet. Deshalb laeuft alles ueber
 * die Schnittstelle SlotSpeicher - ein serverseitiger Speicher ersetzt dann
 * nur diese eine Stelle, ohne dass die Oberflaeche etwas merkt.
 *
 * Sicherheit (Masterprompt Abschnitt 27): Gespeichert werden ausschliesslich
 * Creep-Namen, also reine Spielwerte - nichts Persoenliches. Und Abschnitt 28:
 * Der Server prueft beim Senden ohnehin jede Welle neu. Ein veraenderter
 * Speicher bringt also keinen Vorteil; hier steht nur eine Bequemlichkeit.
 */

import { getCreep } from '@dueline/game-data';
import { emptyDraft, type WaveDraft } from './composerModel.js';

/** So viele Plaetze gibt es. */
export const WAVE_SLOT_COUNT = 3;

/** Ein Platz ist entweder belegt oder leer. */
export type WaveSlots = readonly (WaveDraft | null)[];

/**
 * Woher die Plaetze kommen und wohin sie gehen.
 *
 * Heute ist das der Browser. Spaeter kann es der Server sein - die Oberflaeche
 * kennt nur diese drei Methoden.
 */
export interface SlotSpeicher {
  lesen(): WaveSlots;
  schreiben(slots: WaveSlots): void;
}

/** Der Schluessel im Browserspeicher. Mit Version, falls sich das Format aendert. */
export const SLOT_STORAGE_KEY = 'dueline.waveSlots.v1';

/** Hoechstens so viele Creeps je Reihe werden gelesen - gegen aufgeblaehte Daten. */
const MAX_PRO_REIHE = 40;

/** Drei leere Plaetze. */
export function emptySlots(): WaveSlots {
  return Array.from({ length: WAVE_SLOT_COUNT }, () => null);
}

/** Liest eine einzelne Reihe und wirft alles weg, was kein echter Creep ist. */
function leseReihe(wert: unknown): string[] {
  if (!Array.isArray(wert)) return [];
  return wert
    .filter((eintrag): eintrag is string => typeof eintrag === 'string')
    .filter((creepId) => getCreep(creepId) !== undefined)
    .slice(0, MAX_PRO_REIHE);
}

/**
 * Macht aus dem gespeicherten Text wieder drei Plaetze.
 *
 * Alles, was nicht passt, wird zu einem leeren Platz: kaputtes JSON, eine
 * falsche Anzahl, unbekannte Creeps. Lieber ein leerer Platz als ein Absturz -
 * es geht hier um eine Bequemlichkeit, nicht um Spielstand.
 */
export function parseSlots(rohtext: string | null): WaveSlots {
  if (!rohtext) return emptySlots();

  let gelesen: unknown;
  try {
    gelesen = JSON.parse(rohtext);
  } catch {
    return emptySlots();
  }

  if (!Array.isArray(gelesen)) return emptySlots();

  return Array.from({ length: WAVE_SLOT_COUNT }, (_, index) => {
    const eintrag = gelesen[index] as Record<string, unknown> | null | undefined;
    if (!eintrag || typeof eintrag !== 'object') return null;

    const welle: WaveDraft = {
      front: leseReihe(eintrag.front),
      center: leseReihe(eintrag.center),
      back: leseReihe(eintrag.back),
    };

    // Ein Platz, auf dem nichts mehr uebrig ist, gilt als leer.
    const leer = welle.front.length + welle.center.length + welle.back.length === 0;
    return leer ? null : welle;
  });
}

/** Schreibt die Plaetze als Text - genau die Form, die parseSlots wieder liest. */
export function serialisiereSlots(slots: WaveSlots): string {
  return JSON.stringify(
    Array.from({ length: WAVE_SLOT_COUNT }, (_, index) => {
      const welle = slots[index];
      if (!welle) return null;
      return { front: [...welle.front], center: [...welle.center], back: [...welle.back] };
    }),
  );
}

/** Legt eine Welle auf einen Platz - oder raeumt ihn leer. */
export function setzeSlot(slots: WaveSlots, index: number, welle: WaveDraft | null): WaveSlots {
  if (index < 0 || index >= WAVE_SLOT_COUNT) return slots;
  return Array.from({ length: WAVE_SLOT_COUNT }, (_, i) =>
    i === index
      ? welle
        ? { ...emptyDraft(), ...structuredKopie(welle) }
        : null
      : (slots[i] ?? null),
  );
}

/** Eine eigene Kopie, damit spaetere Aenderungen am Entwurf den Platz nicht veraendern. */
function structuredKopie(welle: WaveDraft): WaveDraft {
  return { front: [...welle.front], center: [...welle.center], back: [...welle.back] };
}

/**
 * Der Speicher im Browser.
 *
 * Er schluckt jeden Fehler: Im privaten Modus oder bei gesperrten Daten wirft
 * localStorage. Das darf das Spiel nicht stoeren - dann gibt es eben keine
 * Plaetze.
 */
export function erstelleLokalenSpeicher(quelle: Storage | null): SlotSpeicher {
  return {
    lesen() {
      try {
        return parseSlots(quelle?.getItem(SLOT_STORAGE_KEY) ?? null);
      } catch {
        return emptySlots();
      }
    },
    schreiben(slots) {
      try {
        quelle?.setItem(SLOT_STORAGE_KEY, serialisiereSlots(slots));
      } catch {
        // Kein Platz, kein Zugriff - dann bleibt es eben bei dieser Sitzung.
      }
    },
  };
}

/**
 * Das hoechste Tier, das diese Welle braucht.
 *
 * Damit sieht man einem Speicherplatz an, ob er ueberhaupt schon schickbar
 * ist - eine Welle mit Tier-III-Creeps nuetzt bei Tier I nichts
 * (Rueckmeldung vom 27.09.2026).
 *
 * Unbekannte Creeps zaehlen nicht mit; sie koennen nach dem Einlesen ohnehin
 * nicht mehr in einem Platz stehen.
 */
export function hoechstesTier(welle: WaveDraft): number {
  const alle = [...welle.front, ...welle.center, ...welle.back];
  return alle.reduce((hoechstes, creepId) => {
    const creep = getCreep(creepId);
    return creep ? Math.max(hoechstes, creep.tier) : hoechstes;
  }, 0);
}
